<template>
  <div class="flex flex-col gap-5 pt-1 pb-[14px]">
    <DesktopSearchSection
      title="Your registrations"
      :loading="privateLoading"
      :total="privateTotal"
      empty-label="No matching registrations or folders"
      col2-label="Location"
      @view-all="goViewAll('files')"
    >
      <DesktopSearchRow
        v-for="item in privateItems"
        :key="item.id"
        :thumb-type="itemThumbType(item)"
        :thumb-src="itemThumbSrc(item)"
        :name="item.name"
        :class="itemDimClass(item)"
        @select="goToItem(item)"
      >
        <template #col2>
          <div v-truncate class="w-full">
            <div data-truncate-target>{{ formatPath(item.parentPath) }}</div>
          </div>
        </template>
      </DesktopSearchRow>
    </DesktopSearchSection>

    <DesktopSearchSection
      title="Public registrations"
      :loading="publicLoading"
      :total="publicTotal"
      empty-label="No matching public registrations"
      col2-label="Dacr Timestamp"
      @view-all="goViewAll('public')"
    >
      <RouterLink
        v-for="file in publicFiles"
        :key="file.id"
        :to="`/file/${file.id}`"
        class="block"
        @click.left.exact="emit('navigate')"
      >
        <DesktopSearchRow
          :thumb-type="fileThumbType(file)"
          :thumb-src="fileThumbSrc(file)"
          :name="file.name"
        >
          <template #col2>
            <DacrMarkIcon class="mr-1.5 size-[14px] shrink-0 text-gray-800" />
            <div v-truncate class="min-w-0 flex-1">
              <div data-truncate-target>
                {{ formatTimestamp(file.timestamp) }}
              </div>
            </div>
          </template>
        </DesktopSearchRow>
      </RouterLink>
    </DesktopSearchSection>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { DacrMarkIcon } from '@dacr/icons'
import {
  ItemType,
  useSearchItemsQuery,
  useSearchFilesQuery,
  type Item,
  type SearchFile,
} from '@/shared/graphql/legacy/generated'
import {
  itemThumbType,
  itemThumbSrc,
  fileThumbType,
  fileThumbSrc,
  formatPath,
  isItemInactive,
  itemDimClass,
} from '@/features/search/utils'
import { formatTimestamp } from '@/shared/utils/datetime'
import DesktopSearchSection from '@/features/search/components/desktop/DesktopSearchSection.vue'
import DesktopSearchRow from '@/features/search/components/desktop/DesktopSearchRow.vue'

const props = defineProps<{ query: string }>()

const emit = defineEmits<{ navigate: [] }>()

const router = useRouter()

const variables = computed(() => ({ query: props.query }))

const { result: privateResult } = useSearchItemsQuery(variables)
const { result: publicResult } = useSearchFilesQuery(variables)

const privateLoading = computed(() => !privateResult.value)
const publicLoading = computed(() => !publicResult.value)

const privateTotal = computed(() => privateResult.value?.searchItems.total)
const publicTotal = computed(() => publicResult.value?.searchFiles.total)

const privateItems = computed<Item[]>(
  () =>
    (privateResult.value?.searchItems.files as Item[] | undefined)?.slice(
      0,
      3
    ) ?? []
)
const publicFiles = computed<SearchFile[]>(
  () =>
    (publicResult.value?.searchFiles.files as SearchFile[] | undefined)?.slice(
      0,
      3
    ) ?? []
)

const goToItem = (item: Item) => {
  if (isItemInactive(item)) return

  if (item.type === ItemType.Folder) {
    router.push(`/home${item.parentPath}/${item.name}`.replace('//', '/'))
  } else if (item.file) {
    router.push(`/file/${item.file.copyrightId}`)
  }
  emit('navigate')
}

const goViewAll = (which: 'files' | 'public') => {
  router.push(`/search/${which}?query=${encodeURIComponent(props.query)}`)
  emit('navigate')
}
</script>
